import React, { useState, useEffect, useRef } from 'react';
import TreeList, {
  Column,
  Paging,
  SearchPanel,
} from 'devextreme-react/tree-list';
import DateBox from 'devextreme-react/date-box';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './PnLTreeList.css';

import { formatNegativeValue } from '../../utils/formatUtils';
import useCobDate from '../../hooks/useCobDate';
import { baseUrl } from '../../hooks/useFetch';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface PnLTreeListProps {
  page?: string;
}

interface TreeRow {
  id: number | string;
  parentId: number | string | null;
  name: string;
  pnl_daily: number;
  pnl_mtd: number;
  pnl_fytd: number;
  sharpe_ratio: number | null;
  sortino_ratio: number | null;
  calmar_ratio: number | null;
  gaintopain_ratio: number | null;
  max_peak_trough_dd: number;
  up_down_days: number | string | null;
  annualised_pnl_vol: number;
  peak_pnl_fytd: number;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PnLTreeList: React.FC<PnLTreeListProps> = ({page}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const expandedOnce = useRef(false);
  const [cobDate, setCobDate, , latestCobDate] = useCobDate("/pnl_latest_cob");
  const [treeData, setTreeData] = useState<TreeRow[]>([]);

    const viewPnl = React.useMemo(() => {
      return treeData;

  }, [cobDate, page,treeData]);

  useEffect(() => {
  if (!cobDate) return;

  const formattedSelectedDate = cobDate.toISOString().split('T')[0];
  const url = `${baseUrl}/pnl/pnltree?pnl_date=${formattedSelectedDate}&pnl_type=${page}`;

  fetch(url)
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data: TreeRow[]) => {
      expandedOnce.current = false;
      setTreeData(data);
    })
    .catch((err) => console.error('Failed to load tree data', err));
}, [cobDate, page]);



  useEffect(() => {
    const interval = setInterval(() => {
      const scrollable = containerRef.current?.querySelector('.dx-treelist .dx-scrollable .dx-scrollable-container') as HTMLElement | null;
      if (!scrollable) return;

      let isDown = false;
      let startX: number;
      let scrollLeft: number;

      const handleMouseDown = (e: MouseEvent) => {
        isDown = true;
        scrollable.classList.add('scrolling-active');
        startX = e.pageX;
        scrollLeft = scrollable.scrollLeft;
      };

      const handleMouseUp = () => {
        isDown = false;
        scrollable.classList.remove('scrolling-active');
      };

      const handleMouseLeave = () => {
        isDown = false;
        scrollable.classList.remove('scrolling-active');
      };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDown) return;
        e.preventDefault();
        const x = e.pageX;
        const walk = (x - startX) * 1.5;
        scrollable.scrollLeft = scrollLeft - walk;
      };

      scrollable.addEventListener('mousedown', handleMouseDown);
      scrollable.addEventListener('mouseup', handleMouseUp);
      scrollable.addEventListener('mouseleave', handleMouseLeave);
      scrollable.addEventListener('mousemove', handleMouseMove);

      clearInterval(interval);
    }, 300);

    return () => clearInterval(interval);
  }, []);

  const renderFirstColumnHeader = () => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px', padding: '8px 0' }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        onMouseLeave={(e) => e.stopPropagation()}
      >
        <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#666' }}>
          COB Date:
        </div>
        <DateBox
          type="date"
          value={cobDate}
          onValueChanged={(e: any) => setCobDate(e.value)}
          placeholder="Select COB Date"
          displayFormat="dd/MM/yyyy"
          width={140}
          height={28}
          max={latestCobDate ?? undefined}
          stylingMode="outlined"  elementAttr={{ class: 'datebox--sm' }}
        />
      </div>
    );
  };

  const handleExcelExport = () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('PnL Report');

    const columns = [
      { header: 'Name', key: 'name', width: 40 },
      { header: 'Daily', key: 'pnl_daily', width: 15 },
      { header: 'MTD', key: 'pnl_mtd', width: 15 },
      { header: 'FYTD', key: 'pnl_fytd', width: 15 },
      { header: 'Sharpe Ratio', key: 'sharpe_ratio', width: 15 },
      { header: 'Sortino Ratio', key: 'sortino_ratio', width: 15 },
      { header: 'Calmar Ratio', key: 'calmar_ratio', width: 15 },
      { header: 'Gain to Pain Ratio', key: 'gaintopain_ratio', width: 18 },
      { header: 'Max Peak to Trough DD', key: 'max_peak_trough_dd', width: 25 },
      { header: 'Up/Down Days', key: 'up_down_days', width: 15 },
      { header: 'Annualised P&L Vol', key: 'annualised_pnl_vol', width: 20 },
      { header: 'Peak FYTD P&L', key: 'peak_pnl_fytd', width: 20 },
    ];

    worksheet.columns = columns;

    viewPnl.forEach((row) => {
      worksheet.addRow({
        name: row.name,
        pnl_daily: row.pnl_daily / 1000,
        pnl_mtd: row.pnl_mtd / 1000,
        pnl_fytd: row.pnl_fytd / 1000,
        sharpe_ratio: row.sharpe_ratio,
        sortino_ratio: row.sortino_ratio,
        calmar_ratio: row.calmar_ratio,
        gaintopain_ratio: row.gaintopain_ratio,
        max_peak_trough_dd: row.max_peak_trough_dd / -1000,
        up_down_days: row.up_down_days,
        annualised_pnl_vol: row.annualised_pnl_vol / 1000,
        peak_pnl_fytd: row.peak_pnl_fytd / 1000,
      });
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'PnL Report.xlsx');
    });
  };

  const handlePdfExport = () => {
    const doc = new jsPDF();

    const columns = [
      ['Name', 'Daily', 'MTD', 'FYTD', 'Sharpe', 'Sortino', 'Calmar', 'G2P', 'Max DD', 'Up/Down', 'Vol', 'Peak'],
    ];

    const rows = viewPnl.map(row => [
      row.name,
      (row.pnl_daily / 1000).toFixed(0),
      (row.pnl_mtd / 1000).toFixed(0),
      (row.pnl_fytd / 1000).toFixed(0),
      row.sharpe_ratio?.toFixed(2),
      row.sortino_ratio?.toFixed(2),
      row.calmar_ratio?.toFixed(2),
      row.gaintopain_ratio?.toFixed(2),
      (row.max_peak_trough_dd / -1000).toFixed(0),
      row.up_down_days,
      (row.annualised_pnl_vol / 1000).toFixed(0),
      (row.peak_pnl_fytd / 1000).toFixed(0),
    ]);

    (doc as any).autoTable({
      head: columns,
      body: rows,
      theme: 'grid',
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 122, 204] },
      margin: { top: 20 },
    });

    doc.save('PnL Report.pdf');
  };

  return (
    <div className="tree-container" ref={containerRef}>
      <TreeList
          elementAttr={{ class: 'treelist-dense' }}
        dataSource={viewPnl}
        keyExpr="id"
        parentIdExpr="parentId"
        showBorders={true}
        //autoExpandAll={true}
        columnAutoWidth={true}
        height={1000}
        scrolling={{
          mode: 'virtual',
          useNative: false,
          showScrollbar: 'always',
        }}
        headerFilter={{ visible: true }}
        onContentReady={(e: any) => {
          if (expandedOnce.current) return;
          const grid = e.component;
          const roots = grid.getVisibleRows().filter((r: any) => r.level === 0);
          if (roots.length === 0) return;
          expandedOnce.current = true;
          roots.forEach((r: any) => grid.expandRow(r.key));
          setTimeout(() => {
            roots.forEach((r: any) => {
              const node = grid.getNodeByKey(r.key);
              const firstChild = node?.children?.[0];
              if (firstChild) grid.expandRow(firstChild.key);
            });
          }, 0);
}}
      >
        <SearchPanel visible={true} />
        <Paging enabled={false} />

        <Column
          dataField="name"
          caption=""
          width={360}
          headerCellRender={renderFirstColumnHeader}
          fixed={true}
          fixedPosition="left"
          allowSorting={false}
        />

        <Column caption="P&L (€'000s)" alignment="center">
          <Column
            dataField="pnl_daily"
            caption="Daily"
            width={100}
            calculateCellValue={(rowData: any) => rowData.pnl_daily / 1000}
            cellRender={({ value }: { value: number }) => formatNegativeValue(value)}
          />
          <Column
            dataField="pnl_mtd"
            caption="MTD"
            width={100}
            calculateCellValue={(rowData: any) => rowData.pnl_mtd / 1000}
            cellRender={({ value }: { value: number }) => formatNegativeValue(value)}
          />
          <Column
            dataField="pnl_fytd"
            caption="FYTD"
            width={100}
            calculateCellValue={(rowData: any) => rowData.pnl_fytd / 1000}
            cellRender={({ value }: { value: number }) => formatNegativeValue(value)}
          />
        </Column>

        <Column caption="Risk adjusted returns" alignment="center">
          <Column dataField="sharpe_ratio" caption="Sharpe ratio" format={{ type: 'fixedPoint', precision: 1 }} />
          <Column dataField="sortino_ratio" caption="Sortino ratio" format={{ type: 'fixedPoint', precision: 1 }} />
          <Column dataField="calmar_ratio" caption="Calmar ratio" format={{ type: 'fixedPoint', precision: 1 }} />
          <Column
            dataField="gaintopain_ratio"
            caption="Gain to pain ratio"
            format={{ type: 'fixedPoint', precision: 1 }}
          />
        </Column>

        <Column caption="Metrics" alignment="center">
          <Column
            dataField="max_peak_trough_dd"
            caption="Max peak to trough DD(€'000s)"
            format={{ type: 'fixedPoint', precision: 0 }}
            calculateCellValue={(rowData: any) => rowData.max_peak_trough_dd / -1000}
          />
          <Column dataField="up_down_days" caption="Up/Down days" format={{ type: 'fixedPoint', precision: 2 }} />
          <Column
            dataField="annualised_pnl_vol"
            caption="Annualised FYTD Realized P&L Vol (€'000s)"
            format={{ type: 'fixedPoint', precision: 0 }}
            calculateCellValue={(rowData: any) => rowData.annualised_pnl_vol / 1000}
          />
          <Column
            dataField="peak_pnl_fytd"
            caption="Peak FYTD P&L (€'000s)"
            format={{ type: 'fixedPoint', precision: 0 }}
            calculateCellValue={(rowData: any) => rowData.peak_pnl_fytd / 1000}
          />
        </Column>
      </TreeList>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
        <button
          onClick={handleExcelExport}
          style={{
            padding: '10px 18px',
            borderRadius: '4px',
            backgroundColor: '#28a745', // green
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Export to Excel
        </button>
        <button
          onClick={handlePdfExport}
          style={{
            padding: '10px 18px',
            borderRadius: '4px',
            backgroundColor: '#d9534f', // red
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Export to PDF
        </button>
      </div>
    </div>
  );
};

export default PnLTreeList;
